import {
  type ChangeEventHandler,
  type KeyboardEventHandler,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { type Form } from '@air/api/types';
import { Label } from '@air/primitive-label';
import { Textarea } from '@air/primitive-textarea';
import { VisuallyHidden } from '@air/primitive-visually-hidden';

import { CONTENT_COLLECTION_FORM_MODAL_EDITABLE_DESCRIPTION } from '~/constants/testIDs';
import { useUpdateForm } from '~/swr-hooks/forms/useUpdateForm';

type FormDescriptionProps = {
  form: Form;
};

export const FormDescription = memo(({ form }: FormDescriptionProps) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { updateForm } = useUpdateForm();
  const [value, setValue] = useState(form.description);

  useEffect(() => {
    setValue(form.description);
  }, [form.description]);

  const onSubmit = useCallback(
    () =>
      updateForm({
        ...form,
        description: value,
      }),
    [form, updateForm, value],
  );

  const onChange = useCallback<ChangeEventHandler<HTMLTextAreaElement>>((event) => {
    setValue(event.target.value);
  }, []);

  const onKeyDown = useCallback<KeyboardEventHandler<HTMLTextAreaElement>>((event) => {
    if (event.key === 'Enter' && event.shiftKey) {
      // Allow newline input when Shift + Enter is pressed
      return;
    }

    if (event.key === 'Enter') {
      textareaRef.current?.blur();
    }
  }, []);

  return (
    <div className="relative flex w-full shrink-0" data-testid={CONTENT_COLLECTION_FORM_MODAL_EDITABLE_DESCRIPTION}>
      <VisuallyHidden>
        <Label htmlFor="form-description">Form description</Label>
      </VisuallyHidden>
      <Textarea
        className="w-full"
        onBlur={onSubmit}
        onChange={onChange}
        onKeyDown={onKeyDown}
        placeholder="Add description here…"
        ref={textareaRef}
        size="extra-large"
        value={value}
      />
    </div>
  );
});

FormDescription.displayName = 'FormDescription';
